<script setup>
import { ref } from 'vue';
import { fieldVariants } from '@/components/ui/variants';

const model = defineModel({ type: String, default: '' });

defineProps({
  label: { type: String, required: true },
  placeholder: { type: String, default: '' },
  size: { type: String, default: 'md' },
  tone: { type: String, default: 'plain' },
  loading: { type: Boolean, default: false },
  autofocus: { type: Boolean, default: false },
  dir: { type: String, default: 'auto' },
});

const emit = defineEmits(['submit', 'keydown', 'focus', 'blur']);

const input = ref(null);
defineExpose({ focus: () => input.value?.focus() });

const iconOffset = { md: 'left-4 text-lg', lg: 'left-4 text-xl', xl: 'left-5 text-2xl' };
</script>

<template>
  <form role="search" :class="fieldVariants({ size, tone })" @submit.prevent="emit('submit', model)">
    <iconify-icon
      :icon="loading ? 'svg-spinners:ring-resize' : 'solar:magnifer-linear'"
      :class="['absolute text-deen-ink/40 pointer-events-none', iconOffset[size]]"
      aria-hidden="true"
    />
    <input
      ref="input"
      v-model="model"
      type="search"
      :dir="dir"
      :aria-label="label"
      :placeholder="placeholder"
      :autofocus="autofocus"
      autocomplete="off"
      spellcheck="false"
      enterkeyhint="search"
      class="peer flex-1 min-w-0 h-full bg-transparent border-0 p-0 text-deen-ink placeholder:text-deen-ink/35 font-medium focus:ring-0 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
      @keydown="emit('keydown', $event)"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
    />
    <button
      v-if="model"
      type="button"
      class="shrink-0 w-8 h-8 mr-1 rounded-full text-deen-ink/40 hover:text-deen-ink hover:bg-deen-ink/5 flex items-center justify-center"
      :aria-label="label"
      @click="model = ''; input?.focus()"
    >
      <iconify-icon icon="solar:close-circle-linear" class="text-lg" aria-hidden="true" />
    </button>
    <slot name="trailing" />
  </form>
</template>
